import * as React from 'react';
import {
  Box,
  Text,
  Image,
  IconButton,
  TextButton,
  Divider,
} from '@wix/design-system';
import { ChevronLeft } from '@wix/wix-ui-icons-common/lazy';
import { AppInfoBadge } from '../../components/app-info-badge';
import type { PricingBadgeProps } from '../../components/pricing-badge';
import type {
  ExternalComponentProps,
  ComponentsWithSiteContextProps,
} from '../types';
import {
  useEssentials,
  withEssentialsContext,
} from '../../contexts/essentials-context';
import { withSiteContext } from '../../contexts/site-context';
import { shouldShowPremiumRequired } from '../../components/app-info-badge/app-info-badge.util';
import type { AppPageStickyOptions } from './types';
import { appPageStickyHeaderDataHooks } from './data-hooks';
import s from './app-page-sticky.module.scss';
import type { BaseApp } from '../../types';
import AppBadges from '../../components/badges/app-badges';
import { isAppWithBenefits } from '@wix/app-market-services';
import { AppReviewsInfo } from '../../components/app-box/app-box-elements/app-reviews-Info/app-reviews-Info';

export interface AppPageStickyProps
  extends Required<
      Pick<
        BaseApp,
        | 'icon'
        | 'company'
        | 'installation'
        | 'pricing'
        | 'name'
        | 'appBadges'
        | 'reviews'
      >
    >,
    ExternalComponentProps,
    ComponentsWithSiteContextProps {
  onBack?: () => void;
  onCompanyClick?: (companyName: string, companySlug: string) => void;
  mainActionButton: React.ReactNode;
  isVisible: boolean;
  isAppUpgradable: boolean;
  isAppCompatible?: boolean;
  options?: AppPageStickyOptions;
  mode?: 'unlisted' | 'test';
}

function AppPageStickyComp({
  isVisible,
  icon,
  onBack,
  onCompanyClick,
  company,
  installation,
  appBadges,
  pricing,
  name,
  mode,
  mainActionButton,
  isPremiumSite,
  isAppUpgradable,
  isAppCompatible = true,
  options = {},
  languageCode,
  reviews = {},
}: AppPageStickyProps) {
  const { t } = useEssentials();
  const {
    showCTA = true,
    showAppInfoBadge = true,
    showBackButton = true,
    showRatingInfoBadge = false,
    showCompany = true,
  } = options;

  const appBadgesElement = (
    <AppBadges
      isInstalled={Boolean(installation?.isInstalled)}
      appName={name ?? ''}
      appBadges={appBadges}
      wixSaleSize="small"
      languageCode={languageCode}
    />
  );

  const infoBadgeElement = (
    <AppInfoBadge
      isAppInstalled={!!installation?.isInstalled}
      isAppBuiltIn={!!installation?.isBuiltIn}
      isAppSetupIncomplete={!!installation?.isSetupIncomplete}
      isAppWithBenefits={isAppWithBenefits(appBadges)}
      isAppCompatible={isAppCompatible}
      pricing={pricing as PricingBadgeProps}
      options={{
        showPremiumRequired: shouldShowPremiumRequired(
          !!installation?.requirements?.premiumSite,
          isPremiumSite ?? false,
        ),
      }}
      dataHook={appPageStickyHeaderDataHooks.APP_STICKY_HEADER_INFO_BADGE}
    />
  );

  const companyElement = (
    <>
      <Text size="small">{t('solution.page.developerName.by')}</Text>
      {onCompanyClick ? (
        <TextButton
          dataHook="company-name-link-sticky-header"
          onClick={() =>
            onCompanyClick(company?.name ?? '', company?.slug ?? '')
          }
          size="small"
        >
          {company?.name}
        </TextButton>
      ) : (
        <Text dataHook="company-name-text-sticky-header" size="small">
          {company?.name}
        </Text>
      )}
    </>
  );

  return (
    <Box
      {...(!isVisible && mode === 'test' && { display: 'none' })}
      {...(!isVisible && mode !== 'test' && { visibility: 'hidden' })}
      verticalAlign="middle"
      position="sticky"
      zIndex="8"
      align="space-between"
      padding={`${showRatingInfoBadge ? '18px 20px' : '18px 24px'}`}
      dataHook={appPageStickyHeaderDataHooks.APP_PAGE_STICKY}
      className={s.appPageStickyHeader}
      maxWidth="1200px"
      margin="0 auto"
    >
      <Box>
        {showBackButton && onBack && (
          <Box
            align="center"
            justifyItems="center"
            verticalAlign="middle"
            marginRight="12px"
            dataHook={
              appPageStickyHeaderDataHooks.APP_STICKY_HEADER_BACK_BUTTON
            }
          >
            <IconButton
              skin="dark"
              priority="tertiary"
              size="tiny"
              onClick={onBack}
            >
              <ChevronLeft size="small" />
            </IconButton>
          </Box>
        )}
        <Box marginRight="12px">
          <Image width="42px" height="42px" src={icon} alt="" />
        </Box>
        <Box direction="vertical">
          <Box>
            <Box>
              <Text size="medium" weight="bold">
                {name}
              </Text>
            </Box>
            <Box className={s.hideOnMobile} paddingLeft="12px" gap="6px">
              {appBadgesElement}
            </Box>
          </Box>
          <Box>
            {showCompany && company?.name && (
              <Box gap="2px">{companyElement}</Box>
            )}
            {showRatingInfoBadge && (
              <Box direction="horizontal" gap="SP2">
                <AppReviewsInfo reviews={reviews} />
                <Divider direction="vertical" />
                {infoBadgeElement}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box className={s.hideOnMobile} gap={isAppUpgradable ? '18px' : '12px'}>
        {showAppInfoBadge && (
          <Box verticalAlign="middle">{infoBadgeElement}</Box>
        )}
        {showCTA && mainActionButton}
      </Box>
    </Box>
  );
}

export const AppPageSticky = withSiteContext(
  withEssentialsContext(AppPageStickyComp),
);
